import { Button } from "@admin/components/atoms/Button";
import { Input } from "@admin/components/atoms/Input";
import { SelectPersonnelsWithGroup } from "@admin/components/molecules/Person";
import { DataTable, DataTableColumn } from "@admin/components/molecules/Table";
import {
  calculateShiftDuration,
  CompanyLocation,
  dateToISO,
  formatDuration,
  getDateRange,
  getHoursAndMinutes,
  getLocationUsers,
  getNormalizedStartEnd,
  getShiftsByUser,
  LocationUser,
  Periods,
  Range,
  Shift,
  UpdateBulkShift,
  updateBulkShift,
  UpdateShiftsDto,
} from "@enos-monorepo/lib";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { CustomPeriod, DeleteShiftModal, Hours } from "../../components";
import { PersonnelViewAddPastShiftModal } from ".";

interface ShiftDto extends Shift {
  selectedDate: string;
}

interface PersonnelViewTableProps {
  selectedLocation?: CompanyLocation;
  companyId: string;
}

export function PersonnelViewTable({
  selectedLocation,
  companyId,
}: PersonnelViewTableProps) {
  const { t, i18n } = useTranslation();
  const [selectedPersonnel, setSelectedPersonnel] = useState<LocationUser>();
  const [selectedRange, setSelectedRange] = useState<Range>(
    getDateRange(Periods.ThisMonth, false),
  );
  const [selectedShift, setSelectedShift] = useState<ShiftDto>();
  const [isDeleteShiftModalOpen, setIsDeleteShiftModalOpen] = useState(false);
  const [addPastShiftModalOpen, setAddPastShiftModalOpen] = useState(false);
  const [editShifts, setEditShifts] = useState<ShiftDto[]>([]);

  const queryClient = useQueryClient();

  const { data: locationUsers, isPending: usersLoading } = useQuery({
    queryKey: ["companyLocations", selectedLocation?.id],
    queryFn: () => getLocationUsers(selectedLocation?.id ?? ""),
    enabled: !!selectedLocation,
  });

  useEffect(() => {
    setSelectedPersonnel(locationUsers?.[0]);
  }, [locationUsers]);

  const { data, isPending: shiftsLoading } = useQuery({
    queryKey: [
      "shifts",
      selectedLocation?.id,
      selectedRange?.start,
      selectedRange?.end,
      selectedPersonnel?.userId,
    ],
    queryFn: () =>
      getShiftsByUser({
        LocationId: selectedLocation?.id ?? "",
        StartDate: selectedRange ? dateToISO(selectedRange?.start) : "",
        EndDate: selectedRange ? dateToISO(selectedRange?.end) : "",
        UserId: selectedPersonnel?.userId ?? "",
      }),
    enabled: !!selectedLocation && !!selectedRange && !!selectedPersonnel,
  });

  const shifts: ShiftDto[] = useMemo(
    () =>
      data
        ?.sort(
          (a, b) =>
            new Date(
              a.plannedStartDate ?? a.workStartDate ?? Date.now(),
            ).getTime() -
            new Date(
              b.plannedStartDate ?? b.workStartDate ?? Date.now(),
            ).getTime(),
        )

        .map((shift) => ({
          ...shift,
          plannedStartDate: getHoursAndMinutes(shift.plannedStartDate),
          plannedEndDate: getHoursAndMinutes(shift.plannedEndDate),
          workStartDate: getHoursAndMinutes(shift.workStartDate),
          workEndDate: getHoursAndMinutes(shift.workEndDate),
          selectedDate: shift.plannedStartDate ?? shift.workStartDate,
        })) as ShiftDto[],
    [data],
  );

  useEffect(() => {
    setEditShifts(shifts ?? []);
  }, [shifts]);

  const { mutate } = useMutation({
    mutationFn: (shift: UpdateBulkShift) => updateBulkShift(shift),
    onSuccess: () => {
      toast.success(t("shift.Shifts have been planned"));
      queryClient.invalidateQueries({ queryKey: ["shifts"] });
    },
    onError: () => {
      toast.error(t("shift.Unable to plan shifts"));
    },
  });

  const onSave = (data: ShiftDto[]) => {
    const bulkshifts: UpdateBulkShift = {
      updateShiftDtos: data.map((shift) => {
        const [startTime, endTime] = getNormalizedStartEnd(
          shift.plannedStartDate,
          shift.plannedEndDate,
          new Date(shift.selectedDate),
        );

        const [workingStartTime, workingEndTime] = getNormalizedStartEnd(
          shift.workStartDate,
          shift.workEndDate,
          new Date(shift.selectedDate),
        );

        return {
          id: shift.id,
          plannedStartTime: startTime ? startTime : shift.plannedStartDate,
          plannedEndTime: endTime ? endTime : shift.plannedEndDate,
          workStartDate: workingStartTime
            ? workingStartTime
            : shift.workStartDate,
          workEndDate: workingEndTime ? workingEndTime : shift.workEndDate,
          breakDuration: shift.breakDuration,
          actualPayment: shift.actualPayment,
          plannedPayment: shift.plannedPayment,
          companyId: companyId,
          companyLocationId: selectedLocation?.id ?? "",
          userId: shift.userId,
        };
      }) as UpdateShiftsDto[],
    };

    mutate(bulkshifts);
  };

  const totalShiftAmount = useMemo(() => {
    return editShifts?.reduce((totalMinutes, shift) => {
      return (
        calculateShiftDuration(
          shift.workStartDate,
          shift.workEndDate,
          shift.breakDuration,
        ) + totalMinutes
      );
    }, 0);
  }, [editShifts]);

  const columns: DataTableColumn<ShiftDto>[] = useMemo(
    () => [
      {
        accessorKey: "selectedDate",
        header: t("shift.Dates"),
        enableSorting: true,
        cell: ({ row }) => {
          return (
            <div className="flex flex-col">
              <span className="font-bold">
                {Intl.DateTimeFormat(i18n.language, {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                }).format(new Date(row.original.selectedDate))}
              </span>
              <span>
                {new Intl.DateTimeFormat(i18n.language, {
                  weekday: "long",
                }).format(new Date(row.original.selectedDate))}
              </span>
            </div>
          );
        },
      },
      {
        header: t("shift.Planned Shift Data"),
        accessorKey: "plannedStartDate",
        cell: ({ row }) => (
          <div className="flex gap-2.5 flex-wrap">
            <Input
              value={row.original.plannedStartDate}
              size="sm"
              type="time"
              className="w-fit"
              onChange={(e) => {
                setEditShifts((prev) =>
                  prev.map((shift) =>
                    shift.id === row.original.id
                      ? { ...shift, plannedStartDate: e.target.value }
                      : shift,
                  ),
                );
              }}
            />
            <Input
              value={row.original.plannedEndDate}
              size="sm"
              className="w-fit"
              type="time"
              onChange={(e) => {
                setEditShifts((prev) =>
                  prev.map((shift) =>
                    shift.id === row.original.id
                      ? { ...shift, plannedEndDate: e.target.value }
                      : shift,
                  ),
                );
              }}
            />
          </div>
        ),
      },
      {
        header: t("shift.Actual Shift Data"),
        accessorKey: "workStartDate",
        cell: ({ row }) => (
          <div className="flex gap-2.5 flex-wrap">
            <Input
              value={row.original.workStartDate}
              size="sm"
              type="time"
              className="w-fit"
              onChange={(e) => {
                setEditShifts((prev) =>
                  prev.map((shift) =>
                    shift.id === row.original.id
                      ? { ...shift, workStartDate: e.target.value }
                      : shift,
                  ),
                );
              }}
            />
            <Input
              value={row.original.workEndDate}
              size="sm"
              className="w-fit"
              type="time"
              onChange={(e) => {
                setEditShifts((prev) =>
                  prev.map((shift) =>
                    shift.id === row.original.id
                      ? { ...shift, workEndDate: e.target.value }
                      : shift,
                  ),
                );
              }}
            />
          </div>
        ),
      },
      {
        header: t("shift.Total Break (Min)"),
        accessorKey: "breakDuration",
        cell: ({ row }) => (
          <div className="w-full">
            <Input
              placeholder="-"
              value={row.original.breakDuration?.toString()}
              size="sm"
              className="w-full"
              onChange={(e) => {
                setEditShifts((prev) =>
                  prev.map((shift) =>
                    shift.id === row.original.id
                      ? { ...shift, breakDuration: +e.target.value }
                      : shift,
                  ),
                );
              }}
            />
          </div>
        ),
      },
      {
        id: "totalAmount",
        header: t("shift.Total Amount"),
        cell: ({ row }) => {
          return (
            <div className="flex flex-row justify-center gap-2.5">
              <Hours>
                {formatDuration(
                  calculateShiftDuration(
                    row.original.workStartDate,
                    row.original.workEndDate,
                    row.original.breakDuration,
                  ),
                )}
              </Hours>
            </div>
          );
        },
      },
      {
        id: t("common.Actions"),
        size: 50,
        cell: () => {
          return (
            <div className="flex flex-row gap-2.5">
              <i
                onClick={() => setIsDeleteShiftModalOpen(true)}
                className="ki-duotone text-red-500 ki-trash-square text-xl cursor-pointer"
              ></i>
            </div>
          );
        },
      },
    ],
    [i18n.language, t],
  );

  return (
    <>
      <DataTable
        loading={usersLoading || shiftsLoading}
        header={
          <div className="flex w-full flex-row justify-between items-center">
            <div className="flex gap-5 w-1/4">
              <SelectPersonnelsWithGroup
                onSelectPersonnel={(personnel) =>
                  setSelectedPersonnel(personnel)
                }
                options={[
                  {
                    title: t("shift.Active Personnels"),
                    options: locationUsers?.filter((lu) => !lu.isDeleted) ?? [],
                  },
                  {
                    title: t("shift.Inactive Personnels"),
                    options: locationUsers?.filter((lu) => lu.isDeleted) ?? [],
                  },
                ]}
                selectedPersonnel={selectedPersonnel ?? null}
              />
            </div>
            <div className="flex flex-row gap-5 items-center h-full">
              <span
                className="flex items-center gap-1 cursor-pointer text-[#296573]"
                onClick={() => setAddPastShiftModalOpen(true)}
              >
                <i className="ki-filled ki-plus-squared"></i>
                <p className="text-xs">{t("shift.Add New")}</p>
              </span>
              <Button
                onClick={() => setEditShifts(shifts ?? [])}
                variant="outline"
                property="danger"
                size="sm"
              >
                {t("common.Discard Changes")}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onSave(editShifts)}
              >
                {t("common.Save Changes")}
              </Button>
              <div className="border-l h-[26px] border-l-gray-300"></div>
              <CustomPeriod
                includeToday={false}
                onSelectRange={(range) => setSelectedRange(range)}
                selectedDateRange={selectedRange}
              />
            </div>
          </div>
        }
        isInfoIcon={false}
        columns={columns}
        data={editShifts}
        onRowClick={setSelectedShift}
        footer={
          <tr key="footer">
            <td>{t("shift.Total Amount")}</td>
            <td />
            <td />
            <td />
            <td>
              <div className="flex justify-center">
                <Hours>{formatDuration(totalShiftAmount)}</Hours>
              </div>
            </td>
            <td />
          </tr>
        }
      />
      <DeleteShiftModal
        open={isDeleteShiftModalOpen}
        onClose={() => setIsDeleteShiftModalOpen(false)}
        shift={selectedShift}
      />
      <PersonnelViewAddPastShiftModal
        open={addPastShiftModalOpen}
        onClose={() => setAddPastShiftModalOpen(false)}
        locationId={selectedLocation?.id ?? ""}
        companyId={companyId}
        selectedPersonnel={selectedPersonnel}
      />
    </>
  );
}
